import { Component, computed, inject, signal, OnInit } from '@angular/core';
import { DecimalPipe, DatePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { MessageModule } from 'primeng/message';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TabsModule } from 'primeng/tabs';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { DeleteLocationResult, EffectiveConfigView, LocationMerchantInfo, LocationTaxes, MethodConfigField, MethodEnvironmentInfo, OnboardedLocation, PaymentMethodInfo, ProvisionResult, StoreEdge } from '../../core/models';
import { edgeStatusLabel, edgeStatusSeverity, edgeStatusTitle, relativeTime } from '../edges/store-edge-status';
import { ProductsTab } from '../merchants/products-tab';
import { XtItemsTab } from '../merchants/xt-items-tab';
import { FamiliesTab } from '../merchants/families-tab';
import { OrdersTab } from '../merchants/orders-tab';
import { PaymentsTab } from '../merchants/payments-tab';

// Dedicated location management page (/locations/:locationId) — replaces the old detail modal so
// merchant data, taxes, and payment-method management have proper room. The payment-method editor is
// an INLINE panel (not a dialog): it opens under the methods table.
//
// The page is organised into tabs: Overview (merchant/Magento/methods/taxes) plus the per-location
// Products, XT Items, Families, Orders, and Payments domains. Every domain tab is LAZY: its heavy
// component is only mounted (@if on the active-tab value) once that tab is first opened, so landing on
// the page fires only Overview's requests — not every domain at once.
//
// PRODUCTS AND XT ITEMS ARE DELIBERATELY ADJACENT. They are the same merchant's item master seen from
// the two sides that operators conflate: what the platform projected from the events it ingested, and
// what the POS itself holds in the replica. Standing on one merchant with both one click apart is the
// whole reason the XT list is here as well as on its own page.
@Component({
    selector: 'app-location-detail',
    standalone: true,
    imports: [CardModule, ButtonModule, TableModule, TagModule, MessageModule, ToggleSwitchModule, SelectModule, SelectButtonModule, InputTextModule, RadioButtonModule, TabsModule, DialogModule, TooltipModule, FormsModule, DecimalPipe, DatePipe, RouterLink, ProductsTab, XtItemsTab, FamiliesTab, OrdersTab, PaymentsTab],
    template: `
        <div class="flex items-center gap-3 mb-4">
            <p-button icon="pi pi-arrow-left" [text]="true" routerLink="/locations" />
            <div>
                <h4 class="m-0">{{ location()?.name ?? 'Location' }}</h4>
                <span class="text-sm text-surface-500">{{ locationId }}</span>
                @if (location(); as l) {
                    <span class="text-sm text-surface-400"> · </span>
                    @if (l.merchantId) {
                        <a class="text-sm text-primary hover:underline" [routerLink]="['/merchants', l.merchantId]">{{ l.merchantName ?? 'Merchant' }}</a>
                    } @else {
                        <span class="text-sm text-surface-400">Not under a merchant</span>
                    }
                }
            </div>
            <div class="ml-auto flex items-center gap-4">
                <!-- IS THE STORE UP? The Edge is the machine in the shop, so the page about a store should
                     say whether the store is running rather than sending the operator to another screen for
                     it. Read once with the page, like everything else here — the live view that refreshes
                     itself is Store Edges, which this links to. -->
                @if (edgesError()) {
                    <span class="text-sm text-surface-400" [attr.title]="edgesError()">Store Edge: unknown</span>
                } @else if (edges().length) {
                    <div class="flex flex-col items-end gap-1">
                        @for (e of edges(); track e.edgeId) {
                            <div class="flex items-center gap-2 text-sm">
                                <span class="text-surface-500">Store Edge</span>
                                <p-tag [value]="edgeStatusLabel(e)" [severity]="edgeStatusSeverity(e)"
                                       [attr.title]="edgeStatusTitle(e)" />
                                <a class="text-primary hover:underline" routerLink="/edges">{{ e.machineName }}</a>
                                <!-- Same coarse wording as the Store Edges page, and dropped entirely for a
                                     machine that was never heard from: the badge already says "Never seen",
                                     and repeating it beside itself says nothing twice. -->
                                @if (e.lastSeenAt) {
                                    <span class="text-surface-400" [attr.title]="e.lastSeenAt | date: 'medium'">
                                        last seen {{ edgeSince(e.lastSeenAt) }}
                                    </span>
                                }
                            </div>
                        }
                    </div>
                } @else if (!edgesLoading()) {
                    <span class="text-sm text-surface-400">No Store Edge registered</span>
                }

                <!-- RETIRING A MERCHANT IS ALSO A BROKER OPERATION, and that is why this is on the page rather
                     than only in the API. Each merchant owns a vhost; the broker enforces a vhost_max, and this
                     platform has already been refused an onboarding with "vhost limit of 10 is reached". A
                     merchant that is gone from the database but whose vhost lingers holds that slot forever, so
                     an operator who cannot find this leaves the ceiling in place. The weight is carried by the
                     confirmation, not by hiding the button. -->
                <p-button label="Delete location" icon="pi pi-trash" severity="danger" [text]="true"
                          (onClick)="openDelete()" />
            </div>
        </div>

        <p-dialog header="Delete location" [(visible)]="deleteOpen" [modal]="true" [style]="{ width: '36rem' }">
            @if (strandedVhost(); as reason) {
                <!-- The record went and the vhost did not. Kept on screen instead of returning to a list
                     where the merchant is simply absent: the slot it still holds is the next onboarding that
                     fails, and nothing else on the platform will mention it. -->
                <p-message severity="warn" styleClass="mb-3">
                    Location deleted, but its vhost was not removed — it still holds a slot against the
                    broker's vhost limit.
                </p-message>
                <p class="text-sm text-surface-500">{{ reason }}</p>
            } @else {
                <p class="text-sm mb-3">
                    Deletes <b>{{ location()?.name || locationId }}</b> and its broker queues; anything still waiting to
                    be processed is discarded and cannot be recovered.
                </p>
                @if (deleteError()) {
                    <p-message severity="error" styleClass="mb-3">{{ deleteError() }}</p-message>
                }
            }
            <ng-template pTemplate="footer">
                @if (strandedVhost()) {
                    <p-button label="Back to locations" (onClick)="leaveAfterDelete()" />
                } @else {
                    <p-button label="Cancel" [text]="true" [disabled]="deleting()"
                              (onClick)="deleteOpen.set(false)" />
                    @if (blockedBy(); as pending) {
                        <!-- The API refused because the queues are not empty. The second press is a DIFFERENT
                             button carrying the count: the operator is no longer confirming a delete, they are
                             agreeing to throw those messages away. -->
                        <p-button [label]="'Delete and discard ' + pending + ' message(s)'" severity="danger"
                                  [loading]="deleting()" (onClick)="confirmDelete(true)" />
                    } @else {
                        <p-button label="Delete location" severity="danger" [loading]="deleting()"
                                  (onClick)="confirmDelete(false)" />
                    }
                }
            </ng-template>
        </p-dialog>

        <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
            <p-tablist>
                <p-tab value="overview">Overview</p-tab>
                <p-tab value="products">Products</p-tab>
                <!-- Immediately after Products, on purpose: the contrast between the projection and the
                     POS's own row only lands if the two are neighbours. -->
                <p-tab value="xt-items">XT Items<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="The POS's own item data. It can differ from Products because each updates on its own schedule." tooltipPosition="top"></i></p-tab>
                <p-tab value="families">Families</p-tab>
                <p-tab value="orders">Orders</p-tab>
                <p-tab value="payments">Payments</p-tab>
            </p-tablist>
            <p-tabpanels>
                <p-tabpanel value="overview">
                    @if (loading()) {
                        <p class="text-sm text-surface-500">Loading…</p>
                    } @else if (error()) {
                        <p-message severity="error">{{ error() }}</p-message>
                    } @else if (location(); as l) {
            @if (!l.magentoInfoAvailable) {
                <!-- Merchant details are stored with the merchant and fetched from Magento only when missing; the last
                     failed attempt's reason is stored too, so it can be shown here without asking Magento again. -->
                <p-message severity="warn" styleClass="mb-3">
                    No merchant details stored yet — showing configuration only.
                    @if (l.merchantDetailsError) { Magento's last answer: {{ l.merchantDetailsError }} }
                    Use "Fetch from Magento" to try again.
                </p-message>
            }
            <!-- The fetch's failure reason, verbatim from the API (Magento's own words). -->
            @if (fetchMagentoError()) {
                <p-message severity="error" styleClass="mb-3">{{ fetchMagentoError() }}</p-message>
            }

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                <p-card>
                    <ng-template pTemplate="title">
                        <div class="flex items-center justify-between">
                            <span class="text-base">Merchant</span>
                            <p-button icon="pi pi-refresh" label="Fetch from Magento" size="small" [text]="true"
                                      [loading]="fetchingMagento()" (onClick)="fetchFromMagento()" />
                        </div>
                    </ng-template>
                    <div class="grid grid-cols-2 gap-2 text-sm">
                        <div><b>Name:</b> {{ l.name ?? '—' }}</div>
                        <div><b>Merchant code:</b> {{ l.merchantCode ?? '—' }}</div>
                        <div><b>Email:</b> {{ l.email ?? '—' }}</div>
                        <div><b>Phone:</b> {{ l.phone ?? '—' }}</div>
                        <div class="col-span-2"><b>Address:</b> {{ l.address ?? '—' }}</div>
                    </div>
                </p-card>

                <p-card>
                    <ng-template pTemplate="title"><span class="text-base">Magento configuration</span></ng-template>
                    <div class="grid grid-cols-2 gap-2 text-sm">
                        <div><b>Store code:</b> {{ l.magentoCode }}</div>
                        <div><b>Magento ID:</b> {{ l.magentoId }}</div>
                        <div><b>Website:</b> {{ l.websiteName ?? '—' }} (#{{ l.magentoWebsiteId }})</div>
                        <div><b>Store group:</b> {{ l.storeGroupName ?? '—' }} (#{{ l.magentoStoreGroupId }})</div>
                        <div><b>Store view:</b> {{ l.storeViewName ?? '—' }} (#{{ l.magentoStoreViewId }})</div>
                        <div><b>Root category:</b> {{ l.rootCategoryId }}</div>
                        <div><b>Inventory source:</b> {{ l.sourceName ?? l.inventorySourceId }}</div>
                        <div><b>Stock:</b> {{ l.stockName ?? l.inventoryStockId }}</div>
                        <div class="flex items-center gap-2 col-span-2">
                            <b>Instant print:</b>
                            @if (l.isInstantPrintEnabled == null) {
                                <span class="text-surface-400">—</span>
                            } @else {
                                <p-tag [value]="l.isInstantPrintEnabled ? 'On' : 'Off'" [severity]="l.isInstantPrintEnabled ? 'success' : 'secondary'" />
                            }
                        </div>
                    </div>
                </p-card>
            </div>

            <!-- A MERCHANT CAN BE CORRECT HERE AND ABSENT FROM THE BROKER. Onboarding deliberately does not
                 roll back when RabbitMQ is unreachable — a blip must not undo a saved merchant — so the vhost
                 is left to a later reconcile pass, and when that pass never lands the store simply stops
                 publishing. The only trace is in the shop's own outbox, as "vhost not found"; nothing on
                 the operator's path said so, and this page could retire a merchant and give its broker slot
                 back but could not fill one. The same repair does exist on the Consumers page, reached by
                 first picking a consumer — which is not what anybody does when told a store went quiet. -->
            <p-card styleClass="mb-4">
                <ng-template pTemplate="title">
                    <div class="flex items-center justify-between">
                        <span class="text-base">Broker<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Provision creates whatever is missing on the broker. Safe to run any time; use it when the store has stopped publishing." tooltipPosition="top"></i></span>
                        <p-button icon="pi pi-wrench" label="Provision" size="small" [text]="true"
                                  [loading]="provisioning()" (onClick)="provision()" />
                    </div>
                </ng-template>

                @if (provisionError()) {
                    <p-message severity="error" styleClass="mb-2">{{ provisionError() }}</p-message>
                }

                @if (provisionResult(); as r) {
                    <!-- Both halves, always. They fail independently, so a vhost that exists with unbound
                         queues is a different state from a merchant with no broker side at all, and an
                         operator who is told only "failed" cannot tell which one they are looking at. -->
                    <div class="flex items-center gap-6 text-sm mb-2">
                        <div class="flex items-center gap-2">
                            <b>Vhost:</b>
                            <p-tag [value]="r.vhostReady ? 'Ready' : 'Not ready'"
                                   [severity]="r.vhostReady ? 'success' : 'secondary'" />
                        </div>
                        <div class="flex items-center gap-2">
                            <b>Queues:</b>
                            <p-tag [value]="r.queuesBound ? 'Bound' : 'Not bound'"
                                   [severity]="r.queuesBound ? 'success' : 'secondary'" />
                        </div>
                    </div>
                    @if (r.error) {
                        @if (r.vhostReady || r.queuesBound) {
                            <p-message severity="warn" styleClass="mb-2">
                                Half of the broker side is in place. The rest was refused.
                            </p-message>
                        } @else {
                            <p-message severity="error" styleClass="mb-2">
                                The broker side was not created.
                            </p-message>
                        }
                        <!-- The broker's sentence, verbatim and on its own line: "vhost limit of 10 is
                             reached" is what tells an operator the ceiling is the problem rather than this
                             merchant, and a friendlier paraphrase would lose exactly that. -->
                        <p class="text-sm text-surface-500">The broker's answer: {{ r.error }}</p>
                    } @else {
                        <p-message severity="success">Provisioned.</p-message>
                    }
                }
            </p-card>

            <p-card styleClass="mb-4">
                <ng-template pTemplate="title">
                    <div class="flex items-center justify-between">
                        <span class="text-base">Payment methods<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="To go live, choose a production environment for the method. Credentials are never displayed." tooltipPosition="top"></i></span>
                        <p-button icon="pi pi-plus" label="Add method" size="small" [text]="true" (onClick)="addMethod()" />
                    </div>
                </ng-template>


                @if (methodsLoading()) {
                    <p class="text-sm text-surface-500">Loading methods…</p>
                } @else if (methodsError()) {
                    <p-message severity="warn" styleClass="mb-2">{{ methodsError() }}</p-message>
                } @else if (methods().length) {
                    <p-table [value]="methods()">
                        <ng-template pTemplate="header">
                            <tr><th>Method</th><th>Enabled</th><th>Primary</th><th>Environment</th><th>Mode</th><th>Updated</th><th></th></tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-m>
                            <tr>
                                <td class="font-medium">{{ m.methodCode }}</td>
                                <td><p-tag [value]="m.enabled ? 'On' : 'Off'" [severity]="m.enabled ? 'success' : 'secondary'" /></td>
                                <td>@if (m.isPrimary) { <p-tag value="Primary" severity="info" /> } @else { <span class="text-surface-400">—</span> }</td>
                                <!-- No environment means the assignment predates them and is still resolving its own
                                     stored config. It works, but nothing on the environments page affects it — which is
                                     exactly why it is called out rather than shown as a blank. -->
                                <td>
                                    @if (m.environmentName) {
                                        {{ m.environmentName }}
                                    } @else {
                                        <p-tag value="Legacy config" severity="warn" />
                                    }
                                </td>
                                <td><p-tag [value]="m.isTest ? 'SANDBOX' : 'PRODUCTION'" [severity]="m.isTest ? 'warn' : 'success'" /></td>
                                <td class="text-sm text-surface-500">{{ m.lastModified ? (m.lastModified | date: 'short') : '—' }}</td>
                                <td><p-button icon="pi pi-pencil" [text]="true" size="small" (onClick)="editMethod(m)" /></td>
                            </tr>
                        </ng-template>
                    </p-table>
                } @else {
                    <p class="text-sm text-surface-500">No payment methods configured. Add one to accept payments for this location.</p>
                }

                <!-- Inline method editor — opens under the table, no modal. -->
                @if (editorOpen()) {
                    <div class="mt-4 border border-surface-200 dark:border-surface-700 rounded-lg p-4 bg-surface-50 dark:bg-surface-800">
                        <div class="flex items-center justify-between mb-3">
                            <h6 class="m-0">{{ editing()?.methodCode ? 'Edit ' + editing()!.methodCode : 'Add payment method' }}</h6>
                            <p-button icon="pi pi-times" [text]="true" size="small" (onClick)="editorOpen.set(false)" />
                        </div>
                        <div class="flex flex-col gap-4">
                            @if (!editing()?.methodCode) {
                                <div>
                                    <label class="block text-sm mb-1">Method</label>
                                    <p-select [options]="knownMethods()" [(ngModel)]="form.methodCode" placeholder="Select a gateway"
                                              styleClass="w-full md:w-96" (onChange)="applyTemplate()" />
                                </div>
                            }
                            <div class="flex flex-wrap items-center gap-6">
                                <div class="flex items-center gap-2"><p-toggleswitch [(ngModel)]="form.enabled" /> <span class="text-sm">Enabled</span></div>
                                <div class="flex items-center gap-2"><p-toggleswitch [(ngModel)]="form.isPrimary" /> <span class="text-sm">Primary (default gateway)</span></div>
                            </div>

                            <!-- THE ENVIRONMENT PICKER. This is the whole configuration surface for an
                                 assignment: the credentials live in the environment, so choosing one is what
                                 decides what this merchant transacts with — and choosing a production one is
                                 what takes them live. -->
                            <div>
                                <label class="block text-sm font-medium mb-1">Environment<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Shared settings for this method, managed on the Payment Methods page." tooltipPosition="top"></i></label>

                                @if (environmentsLoading()) {
                                    <p class="text-sm text-surface-500">Loading environments…</p>
                                } @else if (environments().length) {
                                    <p-select [options]="environments()" [ngModel]="form.environmentId" (ngModelChange)="onEnvironmentChange($event)"
                                              optionLabel="name" optionValue="id"
                                              placeholder="Choose an environment" styleClass="w-full md:w-96" [showClear]="false">
                                        <ng-template let-e pTemplate="item">
                                            <div class="flex items-center gap-2">
                                                <span>{{ e.name }}</span>
                                                <p-tag [value]="e.isProduction ? 'PRODUCTION' : 'SANDBOX'"
                                                       [severity]="e.isProduction ? 'success' : 'warn'" styleClass="text-[10px]" />
                                            </div>
                                        </ng-template>
                                    </p-select>

                                    @if (selectedEnvironment(); as e) {
                                        <div class="flex items-center gap-2 mt-2 text-xs">
                                            <p-tag [value]="e.isProduction ? 'PRODUCTION' : 'SANDBOX'"
                                                   [severity]="e.isProduction ? 'success' : 'warn'" />
                                            <span class="text-surface-500">
                                                @if (e.isProduction) {
                                                    Real money.
                                                } @else {
                                                    Test only — payments cannot move real funds.
                                                }
                                            </span>
                                        </div>
                                    }

                                    @if (editing()?.methodCode && !editing()!.environmentId) {
                                        <p-message severity="warn" styleClass="mt-2 text-xs">
                                            This method uses a <b>legacy configuration</b> that the Payment Methods page does not affect.
                                            Choose an environment to move it over.
                                        </p-message>
                                    }
                                } @else {
                                    <p-message severity="warn" styleClass="text-xs">
                                        This method has no environments yet. Create one on the
                                        <a routerLink="/payment-methods" class="underline">Payment Methods</a> page first.
                                    </p-message>
                                }
                            </div>

                            <!-- ==================== MERCHANT DETAILS ====================
                                 Rendered ENTIRELY from the method's declared form (the merchant-specific
                                 fields the API serves). Nothing here names a MID or a terminal: a future
                                 gateway whose merchant half is three different fields renders correctly with
                                 no change to this file. -->
                            @if (merchantFields().length) {
                                <div class="border border-surface-200 dark:border-surface-700 rounded-lg p-4 bg-surface-0 dark:bg-surface-900">
                                    <h6 class="m-0 mb-1">Merchant details<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="The values that identify this merchant at the processor." tooltipPosition="top"></i></h6>

                                    <div class="flex flex-col gap-2 mb-3">
                                        <label class="flex items-start gap-2 cursor-pointer">
                                            <p-radiobutton name="merchantSource" [value]="false" [ngModel]="form.usesOwnMerchantDetails"
                                                           (ngModelChange)="setUsesOwnMerchantDetails($event)" />
                                            <span class="text-sm leading-tight">
                                                Use environment defaults
                                            </span>
                                        </label>
                                        <label class="flex items-start gap-2 cursor-pointer">
                                            <p-radiobutton name="merchantSource" [value]="true" [ngModel]="form.usesOwnMerchantDetails"
                                                           (ngModelChange)="setUsesOwnMerchantDetails($event)" />
                                            <span class="text-sm leading-tight">
                                                This merchant's own values<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Used in whichever environment is chosen, sandbox or production, so wrong values will fail at the gateway." tooltipPosition="top"></i>
                                            </span>
                                        </label>
                                    </div>

                                    @if (form.usesOwnMerchantDetails) {
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                                            @for (f of merchantFields(); track f.key) {
                                                <div class="mb-2">
                                                    <label class="block text-xs text-surface-600 dark:text-surface-300 mb-1">
                                                        {{ f.label }}
                                                        @if (f.requiredForProduction) { <span class="text-primary font-semibold">*</span> }
                                                        @if (f.configured) {
                                                            <p-tag value="Set" severity="success" styleClass="text-[10px] ml-1" />
                                                        } @else {
                                                            <p-tag value="Not set" severity="secondary" styleClass="text-[10px] ml-1" />
                                                        }
                                                        @if (f.help) { <span class="text-surface-400"> — {{ f.help }}</span> }
                                                    </label>
                                                    <input pInputText [type]="f.secret ? 'password' : 'text'" class="w-full"
                                                           [(ngModel)]="merchantValues[f.key]"
                                                           [name]="'merchant-' + f.key"
                                                           [placeholder]="f.configured ? (f.secret ? '•••••• (unchanged)' : '(unchanged)') : ''" />
                                                </div>
                                            }
                                        </div>
                                    } @else if (selectedEnvironment(); as e) {
                                        <p class="text-xs text-surface-500 dark:text-surface-400">
                                            Payments will use the defaults from <b>{{ e.name }}</b>.
                                        </p>
                                    }
                                </div>
                            }
                            <!-- ==================== EFFECTIVE CONFIGURATION ====================
                                 The materialized blob, redacted — literally what a charge will be handed,
                                 not a re-derivation of it. This is the answer to "I set this one with wrong
                                 credentials and it still worked": whatever is shown here IS what runs. -->
                            @if (effectiveConfig(); as ec) {
                                <div class="border border-surface-200 dark:border-surface-700 rounded-lg p-4 bg-surface-50 dark:bg-surface-800">
                                    <div class="flex items-center justify-between mb-1">
                                        <h6 class="m-0">Effective configuration<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="The settings a payment will actually use, as of the last save. Secrets show only whether they are set." tooltipPosition="top"></i></h6>
                                        <p-button icon="pi pi-refresh" [text]="true" size="small" [loading]="effectiveLoading()"
                                                  (onClick)="loadEffectiveConfig(form.methodCode)" />
                                    </div>
                                    <p class="text-xs text-surface-500 dark:text-surface-400 mb-3">
                                        @if (ec.usesLegacyConfig) {
                                            <b>This assignment still uses its legacy configuration.</b>
                                        } @else if (ec.environmentName) {
                                            From <b>{{ ec.environmentName }}</b> ({{ ec.environmentIsProduction ? 'production' : 'sandbox' }}),
                                            merchant details from <b>{{ ec.usesOwnMerchantDetails ? 'this merchant' : 'the environment' }}</b>.
                                        }
                                    </p>

                                    <table class="w-full text-xs">
                                        @for (f of ec.fields; track f.key) {
                                            <tr class="border-b border-surface-200/60 dark:border-surface-700/60">
                                                <td class="py-1 pr-2 text-surface-600 dark:text-surface-300 whitespace-nowrap">{{ f.label }}</td>
                                                <td class="py-1 pr-2 font-mono break-all">
                                                    @if (!f.configured) {
                                                        <span class="text-surface-400">— not set</span>
                                                    } @else if (f.secret) {
                                                        <p-tag value="Set" severity="success" styleClass="text-[10px]" />
                                                    } @else {
                                                        {{ f.value }}
                                                    }
                                                </td>
                                                <td class="py-1 text-right whitespace-nowrap">
                                                    @if (f.source) {
                                                        <p-tag [value]="f.source" [severity]="f.source === 'merchant' ? 'info' : 'secondary'"
                                                               styleClass="text-[10px]" />
                                                    }
                                                </td>
                                            </tr>
                                        }
                                    </table>
                                </div>
                            } @else if (effectiveLoading()) {
                                <p class="text-sm text-surface-500">Loading effective configuration…</p>
                            }

                            @if (methodSaveError()) { <p-message severity="error">{{ methodSaveError() }}</p-message> }
                            <div class="flex justify-end gap-2">
                                <p-button label="Cancel" [text]="true" (onClick)="editorOpen.set(false)" />
                                <p-button label="Save" icon="pi pi-check" [loading]="methodSaving()" (onClick)="saveMethod()" />
                            </div>
                        </div>
                    </div>
                }
            </p-card>

            <p-card>
                <ng-template pTemplate="title"><span class="text-base">Resolved taxes<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Read live from XT." tooltipPosition="top"></i></span></ng-template>
                @if (taxesLoading()) {
                    <p class="text-sm text-surface-500">Loading taxes…</p>
                } @else if (taxesError()) {
                    <p-message severity="warn" styleClass="mb-2">{{ taxesError() }}</p-message>
                } @else if (taxes()?.groups?.length) {
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        @for (g of taxes()!.groups; track g.code) {
                            <div>
                                <div class="font-medium text-sm mb-1">{{ g.code }} — {{ g.name }}</div>
                                <table class="w-full text-sm">
                                    @for (t of g.taxes; track t.name) {
                                        <tr>
                                            <td class="py-0.5">{{ t.name }}</td>
                                            <td class="py-0.5 text-right">
                                                @if (t.unit) {
                                                    \${{ t.ratePercent | number: '1.2-2' }} per {{ t.unit }}
                                                } @else {
                                                    {{ t.ratePercent | number: '1.0-4' }}%
                                                }
                                            </td>
                                        </tr>
                                    }
                                </table>
                            </div>
                        }
                    </div>
                } @else {
                    <p class="text-sm text-surface-500">No tax configuration found for this location.</p>
                }
            </p-card>
                    }
                </p-tabpanel>

                <p-tabpanel value="products">
                    @if (visited().has('products') && locationId) {
                        <!-- The cross-reference lives HERE, in the host, rather than inside ProductsTab:
                             that component is also mounted on the standalone /products page, where there
                             is no XT Items tab to point at and the equivalent line already sends you to
                             Administration instead. -->
                        <app-products-tab [locationId]="locationId" />
                    }
                </p-tabpanel>

                <p-tabpanel value="xt-items">
                    @if (visited().has('xt-items') && locationId) {
                        <!-- WHAT AN OPERATOR NEEDS TO KNOW STANDING HERE, in three sentences: which side of
                             the comparison this is, what the neighbouring tab is, and that a difference
                             between them is the normal case rather than evidence of a fault. -->
                        <app-xt-items-tab [locationId]="locationId" />
                    }
                </p-tabpanel>

                <p-tabpanel value="families">
                    @if (visited().has('families') && locationId) {
                        <app-families-tab [locationId]="locationId" />
                    }
                </p-tabpanel>

                <p-tabpanel value="orders">
                    @if (visited().has('orders') && locationId) {
                        <app-orders-tab [locationId]="locationId" />
                    }
                </p-tabpanel>

                <p-tabpanel value="payments">
                    @if (visited().has('payments') && locationId) {
                        <app-payments-tab [locationId]="locationId" />
                    }
                </p-tabpanel>
            </p-tabpanels>
        </p-tabs>
    `
})
export class LocationDetail implements OnInit {
    private readonly api = inject(MiddlewareApi);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);

    locationId = '';

    // Active tab + the set of heavy tabs that have been opened at least once. A domain tab's component
    // is only mounted after its tab is first activated (see the @if in the template), so opening the
    // page fires Overview's requests alone — not all three domains at once. 'visited' is monotonic:
    // once a tab is mounted it stays mounted (keeps its own paging/filter state on tab switches).
    readonly activeTab = signal<string>('overview');
    readonly visited = signal<Set<string>>(new Set(['overview']));

    readonly location = signal<OnboardedLocation | null>(null);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);

    // RETIRING A MERCHANT. `blockedBy` holds the message count the API refused over — the destructive form
    // of the action is offered only once it is set, so "discard 900 messages" can never be the first thing
    // a press does. `strandedVhost` is the partial outcome: the record went, the vhost stayed, and the
    // dialog stays open on it because that is the state nothing else will report.
    readonly deleteOpen = signal(false);
    readonly deleting = signal(false);
    readonly deleteError = signal<string | null>(null);
    readonly blockedBy = signal<number | null>(null);
    readonly strandedVhost = signal<string | null>(null);

    // REPAIRING A MERCHANT'S BROKER SIDE, the opposite direction from the delete above. `provisionResult`
    // holds the answer whether or not it worked: a broker that refuses does so with a normal 200 whose
    // `error` carries its own sentence, and a half-finished outcome (vhost there, queues not bound) has to
    // stay readable as its own state rather than collapsing into "failed". `provisionError` is kept apart
    // for the failures that are ours and not the broker's — a missing permission, an unreachable API — so
    // that nothing gets attributed to a broker that never answered.
    readonly provisioning = signal(false);
    readonly provisionResult = signal<ProvisionResult | null>(null);
    readonly provisionError = signal<string | null>(null);

    readonly taxes = signal<LocationTaxes | null>(null);
    readonly taxesLoading = signal(false);
    readonly taxesError = signal<string | null>(null);

    readonly merchant = signal<LocationMerchantInfo | null>(null);

    // This location's Edge (normally one, but the endpoint answers with a list and the header shows a row
    // each — a store that registered a replacement machine has two, and hiding one would be the lie).
    readonly edges = signal<StoreEdge[]>([]);
    readonly edgesLoading = signal(true);
    readonly edgesError = signal<string | null>(null);

    // The wording is shared with the Store Edges page so a store reads the same on both.
    readonly edgeStatusLabel = edgeStatusLabel;
    readonly edgeStatusSeverity = edgeStatusSeverity;
    readonly edgeStatusTitle = edgeStatusTitle;
    readonly edgeSince = relativeTime;

    // On-demand Magento read-through for the Merchant card. The list endpoint's enrichment is
    // best-effort and active-instance-only (its failure reason goes to a log, not the operator) —
    // this button asks Magento again and surfaces the ACTUAL answer: the merchant fields on
    // success, Magento's own words on failure.
    readonly fetchingMagento = signal(false);
    readonly fetchMagentoError = signal<string | null>(null);

    readonly methods = signal<PaymentMethodInfo[]>([]);
    readonly methodsLoading = signal(false);
    readonly methodsError = signal<string | null>(null);
    readonly knownMethods = signal<string[]>([]);

    readonly editorOpen = signal(false);
    readonly editing = signal<PaymentMethodInfo | null>(null);
    readonly methodSaving = signal(false);
    readonly methodSaveError = signal<string | null>(null);
    form: { methodCode: string; enabled: boolean; isPrimary: boolean; environmentId: string | null; usesOwnMerchantDetails: boolean } =
        { methodCode: '', enabled: true, isPrimary: false, environmentId: null, usesOwnMerchantDetails: false };

    // The MERCHANT-SPECIFIC half of the method's form, served by the API. The editor renders whatever it
    // is handed — there is no field list in this file, so a future gateway needs no frontend change.
    readonly merchantFields = signal<MethodConfigField[]>([]);
    merchantValues: Record<string, string> = {};

    // The materialized effective configuration — fetched on open, and replaced by whatever the save
    // returns, so the readout always shows the blob that now exists rather than the one that used to.
    readonly effectiveConfig = signal<EffectiveConfigView | null>(null);
    readonly effectiveLoading = signal(false);

    // The environments the chosen method offers. Loaded per method, because they belong to the METHOD —
    // one location does not get a private set.
    readonly environments = signal<MethodEnvironmentInfo[]>([]);
    readonly environmentsLoading = signal(false);
    readonly selectedEnvironmentId = signal<string | null>(null);
    readonly selectedEnvironment = computed(() =>
        this.environments().find((e) => e.id === this.selectedEnvironmentId()) ?? null);

    ngOnInit(): void {
        this.locationId = this.route.snapshot.paramMap.get('locationId') ?? '';
        // Asked for on its own, ahead of the list: a store can have an Edge registered whether or not it is
        // onboarded in the ACTIVE Magento instance, and "is the store up" is worth answering either way.
        this.loadEdges();
        // No single-location endpoint yet — reuse the list and pick ours.
        this.api.getLocations().subscribe({
            next: (r) => {
                const loc = (r.locations ?? []).find((x) => x.locationId === this.locationId) ?? null;
                this.location.set(loc);
                this.loading.set(false);
                if (!loc) { this.error.set(`Location '${this.locationId}' is not onboarded in the active instance.`); return; }
                this.loadTaxes();
                this.loadMerchant();
                this.loadMethods();
            },
            error: (e) => { this.error.set(this.describe(e)); this.loading.set(false); }
        });
    }

    onTabChange(value: string | number | undefined): void {
        const tab = String(value ?? 'overview');
        this.activeTab.set(tab);
        if (!this.visited().has(tab)) {
            this.visited.update((s) => new Set(s).add(tab));
        }
    }

    fetchFromMagento(): void {
        this.fetchingMagento.set(true);
        this.fetchMagentoError.set(null);
        this.api.fetchLocationFromMagento(this.locationId).subscribe({
            next: (r) => {
                this.fetchingMagento.set(false);
                const l = this.location();
                if (r.success && r.merchant && l) {
                    // The server stored exactly this answer, so the card shows exactly it, and the banner clears.
                    this.location.set({
                        ...l,
                        name: r.merchant.name,
                        merchantCode: r.merchant.merchantCode,
                        email: r.merchant.email,
                        phone: r.merchant.phone,
                        address: r.merchant.address,
                        websiteName: r.merchant.websiteName,
                        storeGroupName: r.merchant.storeGroupName,
                        storeViewName: r.merchant.storeViewName,
                        sourceName: r.merchant.sourceName,
                        stockName: r.merchant.stockName,
                        magentoInfoAvailable: true,
                        merchantDetailsSyncedAt: new Date().toISOString(),
                        merchantDetailsError: null
                    });
                } else if (!r.success) {
                    this.fetchMagentoError.set(r.error ?? 'Magento returned no merchant details.');
                }
            },
            error: (e) => { this.fetchingMagento.set(false); this.fetchMagentoError.set(this.describe(e)); }
        });
    }

    private loadEdges(): void {
        this.edgesLoading.set(true);
        this.api.getStoreEdges(null, this.locationId).subscribe({
            next: (e) => { this.edges.set(e ?? []); this.edgesLoading.set(false); },
            // Kept apart from the page's own error: not knowing whether the store is up must not read as
            // "no Edge registered", and it is not a reason to fail the rest of the page.
            error: (e) => {
                this.edgesError.set(e?.status === 403
                    ? 'Forbidden — your role lacks the merchants:read permission.'
                    : this.describe(e));
                this.edgesLoading.set(false);
            }
        });
    }

    private loadMerchant(): void {
        this.api.getLocationMerchant(this.locationId).subscribe({
            next: (m) => this.merchant.set(m),
            error: () => this.merchant.set(null)
        });
    }

    onEnvironmentChange(environmentId: string | null): void {
        this.form.environmentId = environmentId;
        this.selectedEnvironmentId.set(environmentId);
    }

    private loadMethods(): void {
        this.methods.set([]);
        this.methodsError.set(null);
        this.methodsLoading.set(true);
        this.api.getPaymentMethods(this.locationId).subscribe({
            next: (m) => { this.methods.set(m ?? []); this.methodsLoading.set(false); },
            error: (e) => { this.methodsError.set(this.describe(e)); this.methodsLoading.set(false); }
        });
        this.api.getKnownPaymentMethods(this.locationId).subscribe({ next: (k) => this.knownMethods.set(k ?? []) });
    }

    addMethod(): void {
        this.editing.set(null);
        this.form = { methodCode: '', enabled: true, isPrimary: this.methods().length === 0, environmentId: null, usesOwnMerchantDetails: false };
        this.selectedEnvironmentId.set(null);
        this.environments.set([]);
        this.merchantFields.set([]);
        this.merchantValues = {};
        this.effectiveConfig.set(null);
        this.methodSaveError.set(null);
        this.editorOpen.set(true);
    }

    editMethod(m: PaymentMethodInfo): void {
        this.editing.set(m);
        this.form = {
            methodCode: m.methodCode, enabled: m.enabled, isPrimary: m.isPrimary,
            environmentId: m.environmentId ?? null, usesOwnMerchantDetails: m.usesOwnMerchantDetails
        };
        this.selectedEnvironmentId.set(m.environmentId ?? null);
        this.merchantValues = {};
        this.methodSaveError.set(null);
        this.loadEnvironments(m.methodCode);
        this.loadMerchantForm(m.methodCode);
        this.loadEffectiveConfig(m.methodCode);
        this.editorOpen.set(true);
    }

    /** The materialized config this assignment would charge with. Null for one that does not exist yet. */
    loadEffectiveConfig(methodCode: string): void {
        if (!methodCode) { this.effectiveConfig.set(null); return; }
        this.effectiveLoading.set(true);
        this.api.getEffectiveConfig(this.locationId, methodCode).subscribe({
            next: (v) => { this.effectiveConfig.set(v); this.effectiveLoading.set(false); },
            error: () => { this.effectiveConfig.set(null); this.effectiveLoading.set(false); }
        });
    }

    setUsesOwnMerchantDetails(usesOwn: boolean): void {
        this.form.usesOwnMerchantDetails = usesOwn;
    }

    /** The merchant-specific fields of the method's form, with per-field presence for this assignment. */
    private loadMerchantForm(methodCode: string): void {
        if (!methodCode) { this.merchantFields.set([]); return; }
        this.merchantFields.set([]);
        this.api.getMerchantMethodForm(this.locationId, methodCode).subscribe({
            next: (f) => this.merchantFields.set(f ?? []),
            error: () => this.merchantFields.set([])
        });
    }

    /** The environments the chosen method offers. They belong to the METHOD, so this reloads whenever the
     *  method changes — and a location is never offered a set of its own. */
    private loadEnvironments(methodCode: string): void {
        if (!methodCode) { this.environments.set([]); return; }
        this.environments.set([]);
        this.environmentsLoading.set(true);
        this.api.getAssignableEnvironments(this.locationId, methodCode).subscribe({
            next: (e) => { this.environments.set(e ?? []); this.environmentsLoading.set(false); },
            error: () => { this.environments.set([]); this.environmentsLoading.set(false); }
        });
    }

    applyTemplate(): void {
        this.onEnvironmentChange(null);
        this.merchantValues = {};
        this.loadEnvironments(this.form.methodCode);
        this.loadMerchantForm(this.form.methodCode);
    }

    saveMethod(): void {
        if (!this.form.methodCode) { this.methodSaveError.set('Pick a method.'); return; }

        // Build the overlay from non-empty inputs only — the server merges over stored values, so a blank
        // field means "unchanged" rather than "erase". Sent only when the merchant is on its own values;
        // on defaults there is nothing to write and the stored overlay is left where it is.
        let merchantConfigJson: string | null = null;
        if (this.form.usesOwnMerchantDetails) {
            const obj: Record<string, string> = {};
            for (const f of this.merchantFields()) {
                const v = (this.merchantValues[f.key] ?? '').trim();
                if (v) obj[f.key] = v;
            }
            merchantConfigJson = Object.keys(obj).length ? JSON.stringify(obj) : null;
        }

        this.methodSaving.set(true);
        this.methodSaveError.set(null);

        // One call. The environment, the defaults-vs-own choice and the merchant's details travel WITH the
        // flags, so the server validates and applies them in one transaction — a merchant cannot end up
        // pointed at production with the details it needed for it saved separately, or not at all.
        this.api.upsertPaymentMethod(this.locationId, this.form.methodCode, {
            enabled: this.form.enabled,
            isPrimary: this.form.isPrimary,
            environmentId: this.form.environmentId,
            merchantConfigJson,
            usesOwnMerchantDetails: this.form.usesOwnMerchantDetails
        }).subscribe({
            next: (effective) => {
                this.methodSaving.set(false);
                // The editor STAYS OPEN and shows what the save produced. Closing it here would hide the
                // one thing the operator came to confirm — and the confusion this readout exists to end was
                // precisely somebody believing a save had taken effect when it had not.
                this.effectiveConfig.set(effective);
                this.merchantValues = {};
                this.loadMerchantForm(this.form.methodCode);
                this.loadMethods();
                this.loadMerchant();
            },
            error: (e) => { this.methodSaving.set(false); this.methodSaveError.set(this.describe(e)); }
        });
    }

    private loadTaxes(): void {
        this.taxes.set(null);
        this.taxesError.set(null);
        this.taxesLoading.set(true);
        this.api.getLocationTaxes(this.locationId).subscribe({
            next: (t) => { this.taxes.set(t); this.taxesLoading.set(false); },
            error: (e) => { this.taxesError.set(this.describe(e)); this.taxesLoading.set(false); }
        });
    }

    /** No confirmation, unlike its neighbour below: this creates what is missing and leaves what is there,
     *  so a second press costs nothing — and an operator reaching for it is usually still guessing at the
     *  fault. The server's reconcile pass runs synchronously, so what comes back is the broker state now. */
    provision(): void {
        this.provisioning.set(true);
        this.provisionResult.set(null);
        this.provisionError.set(null);
        this.api.provisionLocation(this.locationId).subscribe({
            next: (r) => {
                this.provisioning.set(false);
                // This is the refusal path too: a broker that says no answers 200 with its reason in `error`.
                this.provisionResult.set(r);
            },
            error: (e) => {
                this.provisioning.set(false);
                // The 404 for a location that was never onboarded answers with a ProvisionResult, which keeps
                // its sentence in `error` — describe() reads `detail`/`message` and would swallow it.
                const body = e?.error as ProvisionResult | undefined;
                this.provisionError.set(e?.status === 403
                    ? 'Forbidden — your role lacks the merchants:manage permission.'
                    : body?.error ?? this.describe(e));
            }
        });
    }

    openDelete(): void {
        this.deleteError.set(null);
        this.blockedBy.set(null);
        this.strandedVhost.set(null);
        this.deleteOpen.set(true);
    }

    confirmDelete(force: boolean): void {
        this.deleting.set(true);
        this.deleteError.set(null);
        this.api.deleteLocation(this.locationId, force).subscribe({
            next: (r) => {
                this.deleting.set(false);
                // Deleted, but the broker kept the vhost: hold the dialog rather than navigating away, since
                // the held slot is invisible everywhere else.
                if (!r.vhostRemoved) {
                    this.strandedVhost.set(r.error ?? 'The broker gave no reason.');
                    return;
                }
                this.leaveAfterDelete();
            },
            error: (e) => {
                this.deleting.set(false);
                const body = e?.error as DeleteLocationResult | undefined;
                // 409 is the refusal-with-a-count, not a failure. Keep the dialog open and let the footer
                // switch to the explicitly destructive button.
                if (e?.status === 409) this.blockedBy.set(body?.messagesDiscarded ?? 0);
                this.deleteError.set(body?.error ?? this.describe(e));
            }
        });
    }

    leaveAfterDelete(): void {
        this.deleteOpen.set(false);
        this.router.navigate(['/locations']);
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the server.';
        if (e?.status === 401) return 'Unauthorized — signing in…';
        if (e?.status === 403) return 'Forbidden — your role lacks the required permission.';
        const server = e?.error?.detail ?? e?.error?.message;
        return server ?? `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
